import * as React from "react";
import { cn } from "@finster/ui";
import { AgentTimeline } from "./AgentActivity/AgentTimeline";
import { Brain, Check, ChevronDown, ChevronRight, Database } from "lucide-react";
import {
  readSkillTurnMetadata,
  type CellUpdate,
  type ChatMessage,
  type ShapeModification,
  type SkillTurnMetadata,
  type SlideModification,
} from "../types";
import { SimpleMarkdown } from "./SimpleMarkdown";
import { ThoughtProcessContent } from "./ThoughtProcessContent";
import { isTaskGeneratedMessage, sanitizeUserFacingText, stripVisionLabel } from "../utils";
import { hasAgentTimelineActivity } from "../services/ai/agentStream";
import {
  SOURCE_CLIENT_LABELS,
  SourceCard,
  extractSourceItemsFromCitations,
  type Citation,
  type SourceItem,
} from "../citations";
import { SourceGroupSection } from "./SourceGroupSection";
import { OutputFeedback } from "../../shared/components/OutputFeedback";
import { UserMessageSkillContent } from "../../shared/skills/components/UserMessageSkillContent";

interface ChatHistoryProps {
  messages: ChatMessage[];
  isLoading?: boolean;
  sessionId?: string;
  /**
   * Called when an inline `<u id="…" …>` citation underline is clicked.
   * Receives the citation pool of the message the underline appeared in
   * so the drawer can resolve the uuid against the right turn's
   * citations (citations are per-message, not per-tab).
   */
  onCitationClick?: (citations: Citation[], id: string) => void;
  /**
   * Called when "View source" is clicked on a Retrieved-sources card.
   * Receives the message's citation pool plus the fully-resolved source
   * item (document or table) so the drawer can render the source
   * directly without back-resolving from a uuid.
   */
  onSourceView?: (citations: Citation[], item: SourceItem) => void;
}

const CollapsibleRow: React.FC<{
  icon: React.ReactNode;
  label: string;
  children: React.ReactNode;
  defaultOpen?: boolean;
}> = ({ icon, label, children, defaultOpen = false }) => {
  const [open, setOpen] = React.useState(defaultOpen);

  return (
    <div>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full cursor-pointer items-center gap-2 rounded-md px-1.5 py-1.5 text-left transition-colors hover:bg-muted/40"
      >
        <span className="flex h-4 w-4 shrink-0 items-center justify-center text-muted-foreground">
          {icon}
        </span>
        <span className="flex-1 text-[13px] font-medium text-foreground">{label}</span>
        <span className="shrink-0 text-muted-foreground/70">
          {open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
        </span>
      </button>
      {/* Body is always mounted inside a `grid-rows-[0fr|1fr]` cell so
          the height transition can interpolate to/from `auto` — no
          fixed max-height needed. `overflow-hidden` on the inner wrapper
          clips the content during the collapse animation. */}
      <div
        className={cn(
          "grid overflow-hidden transition-[grid-template-rows] duration-200 ease-out",
          open ? "grid-rows-[1fr]" : "grid-rows-[0fr]"
        )}
      >
        <div className="min-h-0">
          {/* Body shares the header's `px-1.5 py-2` so margins stay symmetric. */}
          <div className="px-1.5 py-2">{children}</div>
        </div>
      </div>
    </div>
  );
};

const ThinkingContent: React.FC<{
  thinking: string;
  onCitationClick?: (id: string, event: React.MouseEvent<HTMLElement>) => void;
}> = ({ thinking, onCitationClick }) => {
  const sanitized = stripVisionLabel(sanitizeUserFacingText(thinking));
  if (!sanitized) return null;

  // MUST stay in sync with `StreamingThinking` in ChatView.tsx so the
  // live and persisted reasoning panes look identical.
  return <ThoughtProcessContent text={sanitized} onCitationClick={onCitationClick} />;
};

interface ChangeRow {
  // Drives `line-through` styling on delete rows.
  action: "create" | "modify" | "delete" | "insert" | "duplicate";
  description: string;
}

function previewText(text: string): string {
  return text.replace(/\s+/g, " ").trim();
}

function previewCells(cells: CellUpdate[] | undefined): string {
  if (!cells || cells.length === 0) return "";
  const values: string[] = [];
  for (const cell of cells) {
    const t = (cell.text ?? "").replace(/\s+/g, " ").trim();
    if (t) values.push(t);
  }
  return values.join(", ");
}

function describeChartEdit(shape: ShapeModification): string {
  const edit = shape.chartEdit;
  if (!edit) {
    return "Updated chart";
  }
  const parts: string[] = [];
  if (edit.series?.length) {
    parts.push(edit.series.some((series) => series.chartType) ? "series/types" : "data/series");
  }
  if (edit.axes?.length) {
    parts.push("axes");
  }
  if (edit.chartArea || edit.plotArea) {
    parts.push("background");
  }
  if (edit.title || edit.legend) {
    parts.push("title/legend");
  }
  if (parts.length === 0) {
    return "Updated chart";
  }
  return `Updated chart ${parts.join(", ")}`;
}

function describeShape(shape: ShapeModification): ChangeRow {
  if (shape.action === "create") {
    if (shape.imageDataUri || shape.imageBase64 || shape.imageUrl || shape.brandIconId) {
      return { action: shape.action, description: "Added an image" };
    }
    if (shape.chart) {
      return { action: shape.action, description: "Added a chart" };
    }
    if (shape.table) {
      return { action: shape.action, description: "Added a table" };
    }
    if (shape.text !== undefined && shape.text.length > 0) {
      return {
        action: shape.action,
        description: `Added text: "${previewText(shape.text)}"`,
      };
    }
    if (shape.geometricType) {
      return { action: shape.action, description: `Added a ${shape.geometricType} shape` };
    }
    return { action: shape.action, description: "Added a new element" };
  }

  if (shape.action === "delete") {
    return { action: shape.action, description: "Deleted this element" };
  }

  // modify — every branch leads with a plain-English verb (Changed /
  // Updated / Replaced / Cleared / Resized) so the user can scan the
  // action at a glance. We deliberately omit position/size changes;
  // intent belongs in `modifications.summary` (row header), this body
  // is for content.
  if (shape.table) {
    const cells = [...(shape.table.textUpdates ?? []), ...(shape.cellUpdates ?? [])];
    if (cells.length > 0) {
      const preview = previewCells(cells);
      if (preview) {
        return { action: shape.action, description: `Updated table cells: ${preview}` };
      }
      return {
        action: shape.action,
        description: `Updated ${cells.length} table cell${cells.length === 1 ? "" : "s"}`,
      };
    }
    if (shape.table.action === "relayout") {
      return { action: shape.action, description: "Resized table" };
    }
    if (shape.table.structureOps?.length) {
      return { action: shape.action, description: "Restructured table" };
    }
    return { action: shape.action, description: "Updated table" };
  }

  if (shape.chart) {
    return { action: shape.action, description: "Updated chart" };
  }

  if (shape.chartEdit) {
    return { action: shape.action, description: describeChartEdit(shape) };
  }

  if (shape.text !== undefined) {
    if (shape.text.length === 0) {
      return { action: shape.action, description: "Cleared the text" };
    }
    return {
      action: shape.action,
      description: `Changed text to "${previewText(shape.text)}"`,
    };
  }

  if (shape.textReplacements?.length) {
    const n = shape.textReplacements.length;
    const first = shape.textReplacements[0];
    const firstDesc = `Replaced "${previewText(first.find)}" with "${previewText(first.replace)}"`;
    if (n === 1) {
      return { action: shape.action, description: firstDesc };
    }
    // Show the first pair + a "(and N more)" tail so the user sees at
    // least one concrete change without expanding PowerPoint.
    return {
      action: shape.action,
      description: `${firstDesc} (and ${n - 1} more)`,
    };
  }

  // Group style-only updates into one row so fill+font+bold doesn't fan out.
  const styleParts: string[] = [];
  if (shape.fillColor) styleParts.push(`fill ${shape.fillColor}`);
  if (shape.fontSize) styleParts.push(`${shape.fontSize}pt`);
  if (shape.bold === true) styleParts.push("bold");
  if (shape.italic === true) styleParts.push("italic");
  if (styleParts.length > 0) {
    return {
      action: shape.action,
      description: `Changed formatting: ${styleParts.join(", ")}`,
    };
  }

  return { action: shape.action, description: "Updated this element" };
}

function describeSlideAction(slide: SlideModification): ChangeRow | null {
  if (slide.action === "insert") {
    return { action: "insert", description: "Created a new slide" };
  }
  if (slide.action === "duplicate") {
    return { action: "duplicate", description: "Duplicated this slide" };
  }
  if (slide.action === "delete") {
    return { action: "delete", description: "Deleted this slide" };
  }
  return null;
}

function countChanges(slides: SlideModification[]): number {
  return slides.reduce((sum, slide) => {
    const slideActionCount = slide.action && slide.action !== "modify" ? 1 : 0;
    const shapeCount = Array.isArray(slide.shapes) ? slide.shapes.length : 0;
    return sum + slideActionCount + shapeCount;
  }, 0);
}

function buildChangeList(slides: SlideModification[]): ChangeRow[] {
  const changes: ChangeRow[] = [];
  // Only prefix "Slide N:" on multi-slide turns; single-slide turns
  // already show the slide in the row header.
  const isMultiSlide = slides.length > 1;
  for (const slide of slides) {
    const slideNumber = slide.slideNumber ?? slide.index + 1;
    const slideAction = describeSlideAction(slide);
    if (slideAction) {
      const description = isMultiSlide
        ? `Slide ${slideNumber}: ${slideAction.description}`
        : slideAction.description;
      changes.push({ ...slideAction, description });
    }

    const shapes = Array.isArray(slide.shapes) ? slide.shapes : [];
    for (const shape of shapes) {
      const base = describeShape(shape);
      const description = isMultiSlide
        ? `Slide ${slideNumber}: ${base.description}`
        : base.description;
      changes.push({ ...base, description });
    }
  }
  return changes;
}

const AppliedChangesRow: React.FC<{
  totalChanges: number;
  slideLabel: string;
  slides: SlideModification[];
  /**
   * Model-authored natural-language summary of the turn's intent.
   * Used as the row header when present; falls back to the
   * "Applied N changes to …" count for legacy / no-op turns.
   */
  summary?: string;
  onCitationClick?: (id: string, event: React.MouseEvent<HTMLElement>) => void;
}> = ({ totalChanges, slideLabel, slides, summary, onCitationClick }) => {
  const [open, setOpen] = React.useState(false);
  const changes = React.useMemo(() => buildChangeList(slides), [slides]);

  const trimmedSummary = summary?.trim() ?? "";
  const hasSummary = trimmedSummary.length > 0;
  const countText = `${totalChanges} ${totalChanges === 1 ? "change" : "changes"}`;

  return (
    // Brand-accented card (matches `DeckCreationProgress` completed-slide rows).
    <div className="mr-1 overflow-hidden rounded-lg bg-card ring-1 ring-inset ring-primary/20">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full cursor-pointer items-start gap-3 px-4 py-3.5 text-left transition-colors hover:bg-muted/30"
      >
        <div className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-primary bg-primary/10 text-primary">
          <Check className="h-4 w-4" strokeWidth={2.25} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-[13px] font-medium leading-snug text-foreground">
            {hasSummary ? trimmedSummary : `Applied ${countText} to ${slideLabel}`}
          </div>
        </div>
        <span className="mt-1 shrink-0 text-muted-foreground">
          {open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
        </span>
      </button>
      {/* Animated body. Same `grid-rows-[0fr|1fr]` pattern as
          `CollapsibleRow` — height transitions to/from `auto` without a
          fixed max-height. Only wrap in the animator when there's at
          least one change; the empty case never even mounts a body. */}
      {changes.length > 0 && (
        <div
          className={cn(
            "grid overflow-hidden transition-[grid-template-rows] duration-200 ease-out",
            open ? "grid-rows-[1fr]" : "grid-rows-[0fr]"
          )}
        >
          <div className="min-h-0">
            <div className="border-t border-primary/15 px-4 py-2">
              <ol className="divide-y divide-primary/10">
                {changes.map((change, idx) => (
                  <li
                    key={idx}
                    className={cn(
                      "flex items-start gap-3 py-3 text-[12.5px] leading-relaxed",
                      change.action === "delete"
                        ? "text-muted-foreground line-through"
                        : "text-foreground/85"
                    )}
                  >
                    <span
                      aria-hidden="true"
                      className={cn(
                        "mt-px flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[10px] font-semibold tabular-nums",
                        change.action === "delete"
                          ? "bg-muted-foreground/30 text-background"
                          : "bg-primary text-primary-foreground"
                      )}
                    >
                      {idx + 1}
                    </span>
                    <span className="min-w-0 flex-1 break-words">
                      <SimpleMarkdown
                        text={change.description}
                        compact
                        onCitationClick={onCitationClick}
                      />
                    </span>
                  </li>
                ))}
              </ol>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const RetrievedSourcesContent: React.FC<{
  items: SourceItem[];
  citedValuesByUuid?: Record<string, string>;
  onView: (item: SourceItem) => void;
  onCitationView?: (id: string) => void;
}> = ({ items, citedValuesByUuid, onView, onCitationView }) => {
  // Group items by their resolved data-source label, preserving
  // first-seen order so the section ordering stays stable across
  // renders (matches the SlideCitationsView grouping).
  const groups = React.useMemo(() => {
    const order: string[] = [];
    const buckets: Record<string, SourceItem[]> = {};
    for (const item of items) {
      const label = item.label || "Other";
      if (!(label in buckets)) {
        buckets[label] = [];
        order.push(label);
      }
      buckets[label].push(item);
    }
    return order.map((label) => ({ label, items: buckets[label] }));
  }, [items]);

  if (groups.length === 0) return null;

  return (
    <div className="flex flex-col gap-1">
      {groups.map((group) => (
        <SourceGroupSection key={group.label} label={group.label} items={group.items}>
          {group.items.map((item) => (
            <SourceCard
              key={item.id}
              item={item}
              citedValuesByUuid={citedValuesByUuid}
              onView={onView}
              onCitationView={onCitationView}
              compact
            />
          ))}
        </SourceGroupSection>
      ))}
    </div>
  );
};

export const ChatHistory: React.FC<ChatHistoryProps> = ({
  messages,
  isLoading,
  sessionId,
  onCitationClick,
  onSourceView,
}) => {
  const lastVisibleMessageIndex = (() => {
    for (let i = messages.length - 1; i >= 0; i--) {
      if (!isTaskGeneratedMessage(messages[i])) return i;
    }
    return -1;
  })();

  if (lastVisibleMessageIndex === -1) {
    return null;
  }

  // Group consecutive messages into "turns" so we can give a tight
  // gap WITHIN a turn (user msg → retrieved sources → thought process
  // → bubble → applied-changes all read as one exchange) and a
  // slightly bigger gap BETWEEN turns. A new turn starts at every
  // user message; assistant messages join the preceding turn.
  //
  // We pre-bind each message's original index here so the inner render
  // loop can use `entry.idx` directly. Looking it up later via
  // `messages.indexOf(msg)` would be O(n) per message and turn the
  // whole render into O(n²) on long histories.
  type TurnEntry = { msg: (typeof messages)[number]; idx: number };
  type Turn = {
    startIdx: number;
    entries: TurnEntry[];
    skillMetadata?: SkillTurnMetadata;
  };
  const turns: Turn[] = [];
  messages.forEach((msg, idx) => {
    // Deck generation stores a synthetic assistant message in the slide
    // session so the Citations tab can resolve per-slide sources. It is
    // storage plumbing, not user-visible chat history.
    if (isTaskGeneratedMessage(msg)) return;

    const entry: TurnEntry = { msg, idx };
    const skillMetadata = readSkillTurnMetadata(msg.skillMetadata);
    if (msg.role === "user" || turns.length === 0) {
      turns.push({ startIdx: idx, entries: [entry], skillMetadata });
    } else {
      const turn = turns[turns.length - 1];
      turn.entries.push(entry);
      if (!turn.skillMetadata && skillMetadata) {
        turn.skillMetadata = skillMetadata;
      }
    }
  });

  return (
    <div>
      {/* Between-turn spacing — generous so the start of a new user
          message clearly breaks from the previous assistant turn's
          last row (Applied-changes card or final bubble). */}
      <div className="flex flex-col gap-10 py-4">
        {turns.map((turn) => (
          // Within-turn spacing — tight so all rows of one exchange
          // visually cohere. The grouped meta-accordions (Retrieved
          // sources + Thought process) still sit flush inside their
          // own zero-gap wrapper below.
          <div key={`turn-${turn.startIdx}`} className="flex flex-col gap-3">
            {turn.entries.map(({ msg, idx }) => {
              const isUser = msg.role === "user";
              const userSkillMetadata = isUser ? turn.skillMetadata : undefined;
              // The success card (`AppliedChangesRow`) and the failure
              // bubble (`msg.content` patched with the apply-status
              // message) are mutually exclusive. `useQueryHandler`
              // patches `content` to the apply-failed status only on the
              // failure path and leaves `content === ""` on success.
              // Treat any non-empty assistant `content` here as the
              // canonical "apply was not a success" signal so we never
              // render the success-looking card next to a failure
              // bubble — even for chats persisted before the matching
              // `clearModifications` patch landed in
              // `updateAssistantMessageContent`.
              const hasAppliedSuccessfully =
                !isUser && (msg.modifications?.slides?.length ?? 0) > 0 && msg.content === "";
              const hasModifications = hasAppliedSuccessfully;
              const isLastMessage = idx === lastVisibleMessageIndex;
              // Show streaming reasoning only after the assistant turn
              // settles (i.e. while the very last turn is still loading
              // we let `StreamingThinking` own the in-flight display).
              const showThinking =
                !isUser &&
                (Boolean(msg.thinking) || hasAgentTimelineActivity(msg.agentTimeline)) &&
                !(isLastMessage && isLoading);
              const showAppliedChanges = hasModifications && !(isLastMessage && isLoading);
              const totalChanges = hasModifications ? countChanges(msg.modifications!.slides) : 0;
              const touchedSlideIndexes = hasModifications
                ? Array.from(new Set(msg.modifications!.slides.map((slide) => slide.index))).sort(
                    (a, b) => a - b
                  )
                : [];
              const slideNumberByIndex = hasModifications
                ? new Map(
                    msg.modifications!.slides.map((slide) => [
                      slide.index,
                      slide.slideNumber ?? slide.index + 1,
                    ])
                  )
                : new Map<number, number>();
              const slideLabel = hasModifications
                ? touchedSlideIndexes.length === 0
                  ? msg.slideIndex !== undefined
                    ? `Slide ${msg.slideNumber ?? msg.slideIndex + 1}`
                    : "slide"
                  : touchedSlideIndexes.length === 1
                    ? `Slide ${
                        slideNumberByIndex.get(touchedSlideIndexes[0]) ??
                        (msg.slideIndex === touchedSlideIndexes[0] ? msg.slideNumber : undefined) ??
                        touchedSlideIndexes[0] + 1
                      }`
                    : `Slides ${touchedSlideIndexes
                        .map(
                          (slideIndex) =>
                            slideNumberByIndex.get(slideIndex) ??
                            (msg.slideIndex === slideIndex ? msg.slideNumber : undefined) ??
                            slideIndex + 1
                        )
                        .join(", ")}`
                : msg.slideIndex !== undefined
                  ? `Slide ${msg.slideNumber ?? msg.slideIndex + 1}`
                  : "slide";

              // Citations are per-message; bind the click handler so it
              // resolves against this message's pool, not the active
              // turn's.
              //
              // The "Retrieved sources" accordion + inline citation clicks
              // operate on the turn-level retrieved pool. Prefer the dedicated
              // `retrievedCitations`/`retrievedCitedValuesByUuid` (set on the
              // source chat message); fall back to the per-slide
              // `citations`/`citedValuesByUuid` for legacy messages saved
              // before the retrieval/per-slide split, and for synthetic
              // per-slide citation messages.
              const messageCitations =
                (msg.retrievedCitations as Citation[] | undefined) ??
                (msg.citations as Citation[] | undefined) ??
                [];
              const messageCitedValues = (msg.retrievedCitedValuesByUuid ??
                msg.citedValuesByUuid ??
                (isTaskGeneratedMessage(msg) ? {} : undefined)) as
                | Record<string, string>
                | undefined;
              // Filter retrieved-but-not-cited Finster docs: only keep
              // source items the model actually quoted. Legacy turns (no
              // cited-value map) fall back to the unfiltered list
              // so old chat history still renders.
              const quotedUuids = messageCitedValues
                ? new Set<string>(Object.keys(messageCitedValues))
                : undefined;
              const citedSourceItems = extractSourceItemsFromCitations(messageCitations, {
                clientLabels: SOURCE_CLIENT_LABELS,
                quotedUuids,
              });
              const handleMessageCitationClick = onCitationClick
                ? (id: string) => onCitationClick(messageCitations, id)
                : undefined;
              const handleSourceCardOpen = onSourceView
                ? (item: SourceItem) => onSourceView(messageCitations, item)
                : () => {};

              // Bubble text: user messages render their authored
              // content; assistant messages render the auto-generated
              // apply summary ("Successfully applied N modifications:
              // …") for apply turns or whatever free-text content the
              // model wrote for non-apply turns. We no longer carry a
              // separate model-authored prose narrative — citations are
              // surfaced via the Retrieved-sources accordion below,
              // driven by `modifications.citations[]`.
              const bubbleText = isUser ? msg.content : sanitizeUserFacingText(msg.content);
              const showBubble = bubbleText.length > 0 || Boolean(userSkillMetadata);
              const showRetrievedSources = !isUser && citedSourceItems.length > 0;
              const renderUserText = (text: string) => (
                <SimpleMarkdown text={text} compact onCitationClick={handleMessageCitationClick} />
              );

              return (
                <React.Fragment key={msg.id}>
                  {/* Metadata accordions group — Retrieved sources and
                      Thought process render flush against each other as
                      a single "meta" block so they read as one section.
                      The within-turn `gap-1` only kicks in between this
                      group and the bubble / Applied-changes card
                      below. */}
                  {(showRetrievedSources || showThinking) && (
                    <div className="flex flex-col">
                      {showRetrievedSources && (
                        <CollapsibleRow
                          icon={<Database className="h-4 w-4 shrink-0 text-muted-foreground" />}
                          label={`Retrieved sources (${citedSourceItems.length})`}
                        >
                          <RetrievedSourcesContent
                            items={citedSourceItems}
                            citedValuesByUuid={messageCitedValues}
                            onView={handleSourceCardOpen}
                            onCitationView={handleMessageCitationClick}
                          />
                        </CollapsibleRow>
                      )}

                      {showThinking && (
                        <CollapsibleRow
                          icon={<Brain className="h-4 w-4 shrink-0 text-muted-foreground" />}
                          label="Thought process"
                        >
                          {hasAgentTimelineActivity(msg.agentTimeline) ? (
                            <AgentTimeline
                              timeline={msg.agentTimeline!}
                              streaming={false}
                              onCitationClick={handleMessageCitationClick}
                            />
                          ) : (
                            <ThinkingContent
                              thinking={msg.thinking!}
                              onCitationClick={handleMessageCitationClick}
                            />
                          )}
                        </CollapsibleRow>
                      )}
                    </div>
                  )}

                  {/* Message bubble — auto-generated apply summary for
                      apply turns, or the model's free-text content for
                      non-apply turns. Survives `hasModifications` so
                      the acknowledgement remains visible after the
                      apply succeeds (matches Figma frame 13). */}
                  {showBubble && (
                    <div
                      className={cn(
                        "flex flex-col rounded-xl px-3 py-2.5 text-[13px] leading-relaxed text-foreground",
                        isUser
                          ? "ml-5 bg-primary/10 ring-1 ring-inset ring-primary/15"
                          : "mr-5 bg-card shadow-sm ring-1 ring-inset ring-border"
                      )}
                    >
                      {isUser ? (
                        <UserMessageSkillContent
                          content={bubbleText}
                          skills={userSkillMetadata?.skills}
                          renderText={renderUserText}
                        />
                      ) : (
                        <div className="break-words">
                          <SimpleMarkdown
                            text={bubbleText}
                            compact
                            onCitationClick={handleMessageCitationClick}
                          />
                        </div>
                      )}
                    </div>
                  )}

                  {/* Applied changes — expandable card directly below
                      the message bubble. */}
                  {showAppliedChanges && (
                    <AppliedChangesRow
                      totalChanges={totalChanges}
                      slideLabel={slideLabel}
                      slides={msg.modifications!.slides}
                      summary={msg.modifications!.summary}
                      onCitationClick={handleMessageCitationClick}
                    />
                  )}

                  {!isUser &&
                    (showBubble || showAppliedChanges) &&
                    !(isLastMessage && isLoading) && (
                      <OutputFeedback
                        area="PowerPoint"
                        className="mr-5"
                        target={{
                          outputId: msg.id,
                          outputType: "chat_response",
                          surface: "chat",
                          sessionId,
                          queryId: msg.id,
                        }}
                      />
                    )}
                </React.Fragment>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
};
